import React, { useState, useRef } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Button,
  Box,
  Alert,
  IconButton,
  LinearProgress,
  CircularProgress,
} from '@mui/joy';
import { UploadSimple as Upload, X, FileText, FileImage, CheckCircle, XCircle as AlertCircle, ArrowCounterClockwise as RotateCcw } from '@phosphor-icons/react';
import { apiClient } from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';

interface OpportunityDocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId: number;
  isMeasurement?: boolean;
}

type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'failed';

interface FileWithPreview extends File {
  preview?: string;
  status?: FileUploadStatus;
  error?: string;
}

export const OpportunityDocumentUploadModal: React.FC<OpportunityDocumentUploadModalProps> = ({
  open,
  onClose,
  opportunityId,
  isMeasurement = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const allowedExtensions = [
    '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
    '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
    '.ppt', '.pptx', '.odt', '.ods', '.odp'
  ];

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: FileWithPreview[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Check file extension
      const fileName = file.name.toLowerCase();
      const fileExt = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExt)) {
        errors.push(`${file.name} has unsupported file type`);
        return;
      }

      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        errors.push(`${file.name} is too large (max 50MB)`);
        return;
      }

      // Create preview for images
      const fileWithPreview: FileWithPreview = Object.assign(file, {
        status: 'pending' as FileUploadStatus
      });
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileWithPreview.preview = e.target?.result as string;
          setSelectedFiles(prev => [...prev]); // Force re-render
        };
        reader.readAsDataURL(file);
      }

      validFiles.push(fileWithPreview);
    });

    if (errors.length > 0) {
      setUploadError(errors.join(', '));
    } else {
      setUploadError(null);
    }

    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => {
      const file = prev[index];
      // Don't allow removing successfully uploaded files
      if (file.status === 'success') {
        return prev;
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadFiles = async (filesToUpload: FileWithPreview[]) => {
    setIsUploading(true);
    setUploadError(null);
    
    // Process the files that were passed in and aren't already successful
    const filesToProcess = filesToUpload.filter(f => f.status !== 'success');
    const totalFiles = filesToProcess.length;
    
    if (totalFiles === 0) {
      setIsUploading(false);
      return;
    }

    let completedCount = 0;

    // Process each file that needs to be uploaded
    for (const fileToUpload of filesToProcess) {

      // Update status to uploading
      setSelectedFiles(prev => {
        const updated = [...prev];
        const fileIndex = updated.findIndex(f => f.name === fileToUpload.name);
        if (fileIndex !== -1) {
          updated[fileIndex] = Object.assign(updated[fileIndex], { 
            status: 'uploading' as FileUploadStatus, 
            error: undefined 
          });
        }
        return updated;
      });

      try {
        const response = await apiClient.uploadOpportunityDocs(
          opportunityId,
          fileToUpload,
          isMeasurement ? { measurement: true } : undefined
        );
        
        // Update status to success
        setSelectedFiles(prev => {
          const updated = [...prev];
          const fileIndex = updated.findIndex(f => f.name === fileToUpload.name);
          if (fileIndex !== -1) {
            updated[fileIndex] = Object.assign(updated[fileIndex], { 
              status: 'success' as FileUploadStatus, 
              error: undefined 
            });
          }
          return updated;
        });
        
        completedCount++;
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
        
        // Update status to failed with error message
        setSelectedFiles(prev => {
          const updated = [...prev];
          const fileIndex = updated.findIndex(f => f.name === fileToUpload.name);
          if (fileIndex !== -1) {
            updated[fileIndex] = Object.assign(updated[fileIndex], { 
              status: 'failed' as FileUploadStatus, 
              error: errorMessage 
            });
          }
          return updated;
        });
        
        completedCount++;
      }

      // Update progress based on files being processed
      setUploadProgress((completedCount / totalFiles) * 100);
    }

    setIsUploading(false);
    
    // Check if all files succeeded
    setSelectedFiles(prev => {
      const allSucceeded = prev.length > 0 && prev.every(f => f.status === 'success');
      
      if (allSucceeded) {
        setUploadSuccess(true);
        // Refresh documents list
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['opportunityDocs', opportunityId.toString()] });
          // Also invalidate measurement docs if this is a measurement upload
          if (isMeasurement) {
            queryClient.invalidateQueries({ queryKey: ['opportunityMeasurementDocs', opportunityId.toString()] });
          }
        }, 500);
        // Close modal after a short delay
        setTimeout(() => {
          setSelectedFiles([]);
          setUploadSuccess(false);
          setUploadProgress(0);
          onClose();
        }, 1500);
      } else {
        // Refresh documents list for successful uploads
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['opportunityDocs', opportunityId.toString()] });
          // Also invalidate measurement docs if this is a measurement upload
          if (isMeasurement) {
            queryClient.invalidateQueries({ queryKey: ['opportunityMeasurementDocs', opportunityId.toString()] });
          }
        }, 500);
      }
      
      return prev;
    });
  };

  const handleUpload = () => {
    const pendingFiles = selectedFiles.filter(f => f.status !== 'success');
    if (pendingFiles.length === 0) return;
    uploadFiles(pendingFiles);
  };

  const handleRetryFailed = () => {
    const failedFiles = selectedFiles.filter(f => f.status === 'failed');
    if (failedFiles.length === 0) return;
    uploadFiles(failedFiles);
  };

  const handleRetryFile = (index: number) => {
    setSelectedFiles(prev => {
      const file = prev[index];
      if (file.status !== 'failed') return prev;
      
      const updated = [...prev];
      updated[index] = Object.assign(updated[index], { 
        status: 'pending' as FileUploadStatus, 
        error: undefined 
      });
      
      // Start upload for this file
      setTimeout(() => {
        uploadFiles([updated[index]]);
      }, 0);
      
      return updated;
    });
  };

  const handleClose = () => {
    if (isUploading) return;
    
    const hasPendingOrFailed = selectedFiles.some(f => f.status === 'pending' || f.status === 'failed');
    
    if (hasPendingOrFailed && selectedFiles.length > 0) {
      const pendingCount = selectedFiles.filter(f => f.status === 'pending').length;
      const failedCount = selectedFiles.filter(f => f.status === 'failed').length;
      
      if (window.confirm(
        `You have ${pendingCount > 0 ? `${pendingCount} pending` : ''}${pendingCount > 0 && failedCount > 0 ? ' and ' : ''}${failedCount > 0 ? `${failedCount} failed` : ''} upload${(pendingCount + failedCount) > 1 ? 's' : ''}. Are you sure you want to close?`
      )) {
        setSelectedFiles([]);
        setUploadError(null);
        setUploadSuccess(false);
        onClose();
      }
    } else {
      setSelectedFiles([]);
      setUploadError(null);
      setUploadSuccess(false);
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <FileImage size={20} />;
    }
    return <FileText size={20} />;
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600 }}>
        <ModalClose />
        <Typography level="h4">{isMeasurement ? 'Upload Measurements' : 'Upload Documents'}</Typography>

        {/* Success Alert */}
        {uploadSuccess && (
          <Alert color="success" sx={{ mt: 2 }}>
            All files uploaded successfully! Your documents are now available.
          </Alert>
        )}

        {/* Error Alert */}
        {uploadError && (
          <Alert color="danger" sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        )}

        {/* Retry Failed Files Banner */}
        {selectedFiles.some(f => f.status === 'failed') && !isUploading && (
          <Alert 
            color="warning" 
            sx={{ mt: 2 }}
            variant="soft"
            endDecorator={
              <Button
                size="sm"
                variant="soft"
                color="warning"
                onClick={handleRetryFailed}
                startDecorator={<RotateCcw size={16} />}
              >
                Retry Failed
              </Button>
            }
          >
            <Typography level="body-sm">
              {selectedFiles.filter(f => f.status === 'failed').length} file{selectedFiles.filter(f => f.status === 'failed').length !== 1 ? 's' : ''} failed to upload
            </Typography>
          </Alert>
        )}

        {/* Drag and Drop Area */}
        {!uploadSuccess && (
          <Box
            sx={{
              mt: 3,
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.500' : 'divider',
              borderRadius: 'md',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: isDragOver ? 'primary.50' : 'transparent',
              '&:hover': {
                borderColor: 'primary.300',
                backgroundColor: 'background.level1',
              },
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography level="body-lg" sx={{ mb: 1 }}>
              Drag & drop files here, or click to browse
            </Typography>
            <Typography level="body-sm" color="neutral">
              Supported formats: PDF, Images, Word, Excel, PowerPoint, CSV, Text
            </Typography>
            <Typography level="body-xs" color="neutral">
              Maximum file size: 50MB
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={allowedExtensions.join(',')}
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </Box>
        )}

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography level="body-sm" fontWeight="lg" sx={{ mb: 2 }}>
              Selected Files ({selectedFiles.length})
              {selectedFiles.filter(f => f.status === 'success').length > 0 && (
                <Typography component="span" level="body-sm" sx={{ color: 'success.500', ml: 1 }}>
                  ({selectedFiles.filter(f => f.status === 'success').length} uploaded)
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedFiles.map((file, index) => {
                const getStatusIcon = () => {
                  switch (file.status) {
                    case 'success':
                      return <CheckCircle size={20} color="#10b981" />;
                    case 'failed':
                      return <AlertCircle size={20} color="#ef4444" />;
                    case 'uploading':
                      return <CircularProgress size="sm" />;
                    default:
                      return null;
                  }
                };

                const getBorderColor = () => {
                  switch (file.status) {
                    case 'success':
                      return 'success.300';
                    case 'failed':
                      return 'danger.300';
                    case 'uploading':
                      return 'primary.300';
                    default:
                      return 'neutral.200';
                  }
                };

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: getBorderColor(),
                      borderRadius: 'sm',
                      backgroundColor: file.status === 'success' ? 'success.50' : 
                                     file.status === 'failed' ? 'danger.50' : 'background.level1',
                    }}
                  >
                    {file.preview ? (
                      <img
                        src={file.preview}
                        alt={file.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      getFileIcon(file)
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="body-sm" noWrap>
                          {file.name}
                        </Typography>
                        {getStatusIcon()}
                      </Box>
                      <Typography level="body-xs" color="neutral">
                        {formatFileSize(file.size)}
                      </Typography>
                      {file.error && (
                        <Typography level="body-xs" sx={{ color: 'danger.600', mt: 0.5 }}>
                          {file.error}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {file.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="plain"
                          color="primary"
                          onClick={() => handleRetryFile(index)}
                          disabled={isUploading}
                          startDecorator={<RotateCcw size={14} />}
                        >
                          Retry
                        </Button>
                      )}
                      {file.status !== 'success' && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => removeFile(index)}
                          disabled={isUploading}
                        >
                          <X size={16} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <Box sx={{ mt: 3 }}>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              Uploading files... {Math.round(uploadProgress)}%
            </Typography>
            <LinearProgress determinate value={uploadProgress} />
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            disabled={isUploading}
          >
            {selectedFiles.every(f => f.status === 'success') ? 'Close' : 'Cancel'}
          </Button>
          {selectedFiles.some(f => f.status !== 'success') && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleUpload}
              disabled={selectedFiles.filter(f => f.status !== 'success').length === 0 || isUploading}
              startDecorator={<Upload size={16} />}
            >
              {isUploading 
                ? `Uploading... ${Math.round(uploadProgress)}%` 
                : `Upload ${selectedFiles.filter(f => f.status !== 'success').length} File${selectedFiles.filter(f => f.status !== 'success').length !== 1 ? 's' : ''}`
              }
            </Button>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};